import React, { useState, useEffect } from 'react';
import { X, Save, Edit } from 'lucide-react';

const DEFAULT_ROLE_OPTIONS = [
  { value: 'membre', label: 'Membre' },
  // { value: 'admin', label: 'Administrateur' },
  { value: 'moderateur', label: 'Modérateur' },
  { value: 'visiteur', label: 'Visiteur' }

];

const EditMemberModal = ({ isOpen, onClose, onSubmit, member, fraternities = [], isLoading = false, roleOptions = DEFAULT_ROLE_OPTIONS }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    profession: '',
    fraternity: '',
    status: 'actif',
    role: 'membre',
    contribution_monthly: '',
    join_date: '',
    couple_name: '',
    marriage_date: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Pré-remplir le formulaire avec les données du membre
  useEffect(() => {
    if (member && isOpen) {
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
        password: '', // Toujours vide pour la sécurité
        password_confirmation: '', // Toujours vide pour la sécurité
        phone: member.phone || '',
        date_of_birth: member.date_of_birth ? member.date_of_birth.split('T')[0] : '',
        gender: member.gender || '',
        address: member.address || '',
        profession: member.profession || '',
        fraternity: member.fraternity || '',
        status: member.status || 'actif',
        role: member.role || 'membre',
        contribution_monthly: member.contribution_monthly || '',
        join_date: member.join_date ? member.join_date.split('T')[0] : '',
        couple_name: member.couple_name || '',
        marriage_date: member.marriage_date ? member.marriage_date.split('T')[0] : '',
        notes: member.notes || ''
      });
      setErrors({});
    }
  }, [member, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Le prénom est requis';
    if (!formData.last_name.trim()) newErrors.last_name = 'Le nom est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email invalide';
    
    // Validation conditionnelle du mot de passe (seulement si rempli)
    if (formData.password.trim()) {
      if (formData.password.length < 6) newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      if (formData.password !== formData.password_confirmation) newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    } else if (formData.password_confirmation.trim()) {
      newErrors.password = 'Veuillez saisir un mot de passe';
    }
    
    if (!formData.fraternity) newErrors.fraternity = 'Sélectionnez une fraternité';
    if (formData.contribution_monthly && isNaN(parseFloat(formData.contribution_monthly))) {
      newErrors.contribution_monthly = 'Montant invalide';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Préparer les données à envoyer
      const submitData = {
        ...formData,
        contribution_monthly: formData.contribution_monthly ? parseFloat(formData.contribution_monthly) : 0,
        // S'assurer que les champs obligatoires sont présents
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        fraternity: formData.fraternity,
        status: formData.status || 'actif',
        role: formData.role || 'membre'
      };
      
      // Ne pas envoyer le mot de passe s'il est vide
      if (!submitData.password?.trim()) {
        delete submitData.password;
        delete submitData.password_confirmation;
      }
      
      console.log('Données à mettre à jour:', submitData);
      onSubmit(member.id, submitData);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      address: '',
      profession: '',
      fraternity: '',
      status: 'actif',
      role: 'membre',
      contribution_monthly: '',
      join_date: '',
      couple_name: '',
      marriage_date: '',
      notes: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    setErrors({});
    onClose();
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Edit className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Modifier le Membre</h2>
              <p className="text-sm text-gray-500">
                {member.first_name} {member.last_name}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nom et Prénom */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prénom *
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({...prev, first_name: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.first_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Entrez le prénom"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom *
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({...prev, last_name: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.last_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Entrez le nom"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="exemple@email.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Mot de passe et Confirmation (optionnel pour modification) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau mot de passe (optionnel)
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Laisser vide pour ne pas modifier"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le nouveau mot de passe
              </label>
              <input
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData(prev => ({...prev, password_confirmation: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.password_confirmation ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Confirmer le nouveau mot de passe"
              />
              {errors.password_confirmation && (
                <p className="mt-1 text-sm text-red-600">{errors.password_confirmation}</p>
              )}
            </div>
          </div>

          {/* Téléphone et Fraternité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="+229 XX XX XX XX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fraternité *
              </label>
              <select
                value={formData.fraternity}
                onChange={(e) => setFormData(prev => ({...prev, fraternity: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.fraternity ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner une fraternité</option>
                {fraternities.map((fraternity) => (
                  <option key={fraternity} value={fraternity}>
                    {fraternity}
                  </option>
                ))}
              </select>
              {errors.fraternity && (
                <p className="mt-1 text-sm text-red-600">{errors.fraternity}</p>
              )}
            </div>
          </div>

          {/* Date de naissance et Genre */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de naissance
              </label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData(prev => ({...prev, date_of_birth: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genre
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData(prev => ({...prev, gender: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Sélectionner</option>
                <option value="masculin">Masculin</option>
                <option value="feminin">Féminin</option>
              </select>
            </div>
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adresse
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={2}
              placeholder="Adresse complète"
            />
          </div>

          {/* Profession et Rôle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profession
              </label>
              <input
                type="text"
                value={formData.profession}
                onChange={(e) => setFormData(prev => ({...prev, profession: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Profession"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({...prev, role: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {roleOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                {formData.role && !roleOptions.some(option => option.value === formData.role) && (
                  <option value={formData.role}>{formData.role}</option>
                )}
              </select>
            </div>
          </div>

          {/* Informations Couple */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du couple
              </label>
              <input
                type="text"
                value={formData.couple_name}
                onChange={(e) => setFormData(prev => ({...prev, couple_name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Nom du couple"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de mariage
              </label>
              <input
                type="date"
                value={formData.marriage_date}
                onChange={(e) => setFormData(prev => ({...prev, marriage_date: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Statut, Cotisation et Date d'adhésion */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="actif">Actif</option>
                <option value="inactif">Inactif</option>
                <option value="suspendu">Suspendu</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cotisation mensuelle (F CFA)
              </label>
              <input
                type="number"
                value={formData.contribution_monthly}
                onChange={(e) => setFormData(prev => ({...prev, contribution_monthly: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.contribution_monthly ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="50000"
                min="0"
              />
              {errors.contribution_monthly && (
                <p className="mt-1 text-sm text-red-600">{errors.contribution_monthly}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date d'adhésion
              </label>
              <input
                type="date"
                value={formData.join_date}
                onChange={(e) => setFormData(prev => ({...prev, join_date: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={3}
              placeholder="Notes complémentaires"
            />
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberModal;